import { Icon } from '../components/Icon';
import { Button as UIButton } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { cn } from '../lib/utils';
import { m } from '../paraglide/messages';
import { getLocale } from '../paraglide/runtime';
import { ENDONYM, SHORT_ENDONYM } from './endonym';
import { useLanguageLinks } from './useLanguageLinks';

export interface HeaderLanguageMenuProps {
  className?: string;
}

/**
 * The header's language button: a globe the size of the theme toggle below
 * `lg`, where the centred links leave no room, and the current short name with
 * a chevron from `lg` up. Either opens this page's languages, the current one
 * ticked. Nothing on an untranslated page.
 */
export const HeaderLanguageMenu = ({ className }: HeaderLanguageMenuProps) => {
  const links = useLanguageLinks();
  if (links === null) return null;
  const current = getLocale();
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <UIButton
          type="button"
          variant="line"
          aria-label={m.language_switcher_current({ language: ENDONYM[current] })}
          className={cn(
            'size-8 gap-1 rounded-8 px-0 text-13 font-medium text-text-2 hover:text-text lg:w-auto lg:px-2',
            className,
          )}
        >
          <Icon name="globe" size={16} className="lg:hidden" />
          <span lang={current} className="hidden lg:inline">
            {SHORT_ENDONYM[current]}
          </span>
          <Icon name="chevron-down" size={14} className="hidden lg:inline" />
        </UIButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-44">
        {links.map(({ locale, href, current: isCurrent }) => (
          <DropdownMenuItem key={locale} asChild className="text-14">
            <a
              href={href}
              hrefLang={locale}
              lang={locale}
              aria-current={isCurrent ? 'page' : undefined}
              className={isCurrent ? 'font-medium text-text' : 'text-text-2'}
            >
              {ENDONYM[locale]}
              {isCurrent ? <Icon name="check" size={14} className="ml-auto text-accent" /> : null}
            </a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
